import React, { useState, useEffect } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { nav, footer as footerData } from "@/content/site";
import { Toaster } from "@/components/ui/sonner";

/* ============================================================
   Nova Layout — Theme C shell shared by every page.
   Injects fonts, renders header (status bar + nav) and footer.
   ============================================================ */

function NovaFonts() {
  return (
    <style>{`
      @import url('https://api.fontshare.com/v2/css?f[]=satoshi@400,500,700,900&display=swap');
      @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=JetBrains+Mono:wght@400;500;600&display=swap');

      .theme-c { background: #FFFFFF; color: #0A0E1A; font-family: 'DM Sans', system-ui, sans-serif; letter-spacing: -0.01em; }
      .theme-c .display { font-family: 'Satoshi', 'DM Sans', sans-serif; letter-spacing: -0.035em; }
      .theme-c .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
      .theme-c .grad-text {
        background: linear-gradient(95deg, #4F46E5 0%, #06B6D4 45%, #F97066 100%);
        -webkit-background-clip: text; background-clip: text; color: transparent;
      }
      .theme-c .grad-bg { background: linear-gradient(120deg, #4F46E5 0%, #06B6D4 50%, #F97066 100%); }
      .theme-c .panel { background: #F5F6FA; }
      .theme-c .panel-2 { background: #ECEEF7; }
      .theme-c .hairline { border-color: #E4E6EE; }
      .theme-c ::selection { background: #4F46E5; color: #fff; }

      @keyframes themec-blob {
        0% { transform: translate(0,0) scale(1); }
        33% { transform: translate(60px,-40px) scale(1.1); }
        66% { transform: translate(-40px,30px) scale(0.95); }
        100% { transform: translate(0,0) scale(1); }
      }
      .theme-c .blob { animation: themec-blob 18s ease-in-out infinite; }

      @keyframes themec-pulse {
        0%,100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.4; transform: scale(1.6); }
      }
      .theme-c .pulse-dot { animation: themec-pulse 1.8s ease-in-out infinite; }

      @keyframes themec-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      .theme-c .up { animation: themec-up 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }

      @keyframes themec-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .theme-c .shimmer {
        background: linear-gradient(90deg, #0A0E1A 25%, #4F46E5 50%, #0A0E1A 75%);
        background-size: 200% 100%;
        -webkit-background-clip: text; background-clip: text; color: transparent;
        animation: themec-shimmer 6s linear infinite;
      }

      @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      .theme-c .marquee-c { animation: marquee 35s linear infinite; }

      .theme-c .card-hover { transition: transform .35s cubic-bezier(.22,1,.36,1), box-shadow .35s, border-color .35s; }
      .theme-c .card-hover:hover { transform: translateY(-4px); box-shadow: 0 22px 48px -24px rgba(79,70,229,0.25); border-color: #4F46E5; }

      .theme-c .grad-border {
        position: relative; background: #fff; border-radius: 20px;
      }
      .theme-c .grad-border::before {
        content: ''; position: absolute; inset: 0; padding: 1px; border-radius: 20px;
        background: linear-gradient(120deg, #4F46E5, #06B6D4, #F97066);
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none;
        opacity: 0; transition: opacity .35s;
      }
      .theme-c .grad-border:hover::before { opacity: 1; }
      .theme-c .grad-border.grad-border-on::before { opacity: 1; }
      .theme-c .grad-border.grad-border-on { border: 0; }

      .theme-c input:focus, .theme-c textarea:focus, .theme-c select:focus { outline: 2px solid #4F46E5; outline-offset: 2px; }
    `}</style>
  );
}

export function NovaMark({ size = 28 }) {
  const id = "nova-grad-" + Math.random().toString(36).slice(2, 7);
  return (
    <svg width={size} height={size} viewBox="0 0 1000 1000" aria-label="Hubinall logo" data-testid="hubinall-logo">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1000" y2="1000" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#4F46E5" />
          <stop offset="55%" stopColor="#06B6D4" />
          <stop offset="100%" stopColor="#F97066" />
        </linearGradient>
      </defs>
      <g fill={`url(#${id})`}>
        {/* Outer frame */}
        <rect x="304" y="194" width="32" height="612" />
        <rect x="304" y="194" width="372" height="32" />
        <rect x="304" y="774" width="372" height="32" />
        {/* Right edge split */}
        <rect x="644" y="194" width="32" height="166" />
        <rect x="644" y="640" width="32" height="166" />
        {/* Inner H */}
        <rect x="424" y="360" width="32" height="280" />
        <rect x="596" y="360" width="32" height="280" />
        <rect x="424" y="484" width="204" height="32" />
      </g>
    </svg>
  );
}

function StatusBar() {
  return (
    <div className="border-b hairline bg-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-9 flex items-center justify-between mono text-[10.5px] tracking-[0.12em]">
        <div className="flex items-center gap-2 text-[#0A0E1A]/70">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[#22C55E] pulse-dot" />
            <span className="relative rounded-full bg-[#22C55E] w-2 h-2" />
          </span>
          <span>All systems operational · Kosovo · Türkiye · United Kingdom</span>
        </div>
        <div className="hidden md:flex items-center gap-4 text-[#0A0E1A]/60">
          <span>v.2026.04</span>
          <Link to="/theme-a" className="hover:text-[#4F46E5]">Theme A</Link>
          <Link to="/theme-b" className="hover:text-[#4F46E5]">Theme B</Link>
        </div>
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <header className="sticky top-0 z-40 border-b hairline bg-white/85 backdrop-blur-xl" data-testid="nova-header">
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 lg:px-14 h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <NovaMark size={30} />
          <span className="display text-2xl font-bold">Hubinall</span>
        </Link>
        <nav className="hidden xl:flex items-center gap-7" data-testid="nav-primary">
          {nav.slice(1).map((n) => (
            <NavLink key={n.path} to={n.path}
              className={({isActive}) => "text-[13.5px] font-medium hover:text-[#4F46E5] transition-colors " + (isActive ? "text-[#4F46E5]" : "text-[#0A0E1A]/80")}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <Link to="/contact?form=call" className="grad-bg text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:opacity-90 shadow-[0_8px_24px_-8px_rgba(79,70,229,0.5)] transition-opacity" data-testid="nav-cta-book-demo">
            Book a Demo →
          </Link>
        </div>
        <button onClick={() => setOpen(v=>!v)} className="xl:hidden w-10 h-10 rounded-xl border hairline flex items-center justify-center" data-testid="nav-mobile-toggle">
          <span className="flex flex-col gap-1.5">
            <span className="block w-4 h-px bg-[#0A0E1A]"/><span className="block w-4 h-px bg-[#0A0E1A]"/><span className="block w-4 h-px bg-[#0A0E1A]"/>
          </span>
        </button>
      </div>
      {open && (
        <div className="xl:hidden border-t hairline">
          <div className="px-6 py-5 flex flex-col">
            {nav.map(n => <NavLink key={n.path} to={n.path} className="py-3 border-b hairline text-base">{n.label}</NavLink>)}
            <Link to="/contact?form=call" className="mt-4 grad-bg text-white rounded-full px-5 py-3 text-sm font-semibold text-center">Book a Demo →</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t hairline bg-white" data-testid="nova-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b hairline">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <NovaMark size={30}/>
              <span className="display text-2xl font-bold">Hubinall</span>
            </div>
            <p className="text-sm leading-relaxed text-[#0A0E1A]/70 max-w-md">{footerData.brand}</p>
            <p className="mono text-[11px] uppercase tracking-[0.18em] grad-text mt-6 font-semibold">{footerData.brandNote}</p>
            <div className="mt-8 panel rounded-2xl p-5">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0A0E1A]/55 mb-2">Stay in the loop</div>
              <div className="flex gap-2">
                <input type="email" placeholder="you@company.com" className="flex-1 bg-white rounded-xl border hairline px-4 py-2.5 text-sm" data-testid="footer-newsletter-email"/>
                <button className="grad-bg text-white rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="footer-newsletter-submit">Subscribe</button>
              </div>
            </div>
          </div>
          {footerData.columns.map(col => (
            <div key={col.title} className="lg:col-span-2">
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0A0E1A]/50 mb-5">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map(l => <li key={l.to}><Link to={l.to} className="text-sm text-[#0A0E1A]/80 hover:text-[#4F46E5]">{l.label}</Link></li>)}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-1">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0A0E1A]/50 mb-5">Contact</div>
            <ul className="space-y-3 text-sm text-[#0A0E1A]/80">
              <li><a href="mailto:hi@hubinall.com" className="hover:text-[#4F46E5]">hi@hubinall.com</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center">
          <div className="flex flex-wrap gap-x-5 gap-y-2 mono text-[10px] uppercase tracking-[0.15em] text-[#0A0E1A]/50">
            {footerData.legalLinks.map(l => (
              l.to.startsWith("#") ? (
                <button key={l.label} onClick={() => { localStorage.removeItem("hubinall_cookie_prefs"); window.location.reload(); }} className="hover:text-[#4F46E5]">{l.label}</button>
              ) : (
                <Link key={l.to} to={l.to} className="hover:text-[#4F46E5]">{l.label}</Link>
              )
            ))}
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-[#0A0E1A]/45">{footerData.copyright}</div>
        </div>
      </div>
    </footer>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => { if (!localStorage.getItem("hubinall_cookie_prefs")) setShow(true); }, []);
  const choose = (v) => { localStorage.setItem("hubinall_cookie_prefs", v); setShow(false); };
  if (!show) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-3xl w-[calc(100%-2rem)] bg-white border hairline rounded-2xl shadow-2xl p-5 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="cookie-banner">
      <p className="text-sm text-[#0A0E1A]/75 flex-1">{footerData.cookieBanner}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => choose("all")} className="grad-bg text-white rounded-full px-4 py-2 text-xs font-semibold" data-testid="cookie-accept-all">Accept All</button>
        <button onClick={() => choose("essential")} className="border hairline rounded-full px-4 py-2 text-xs font-semibold" data-testid="cookie-reject">Reject Non-Essential</button>
        <button onClick={() => choose("manage")} className="text-[#0A0E1A]/60 text-xs font-semibold px-3 py-2" data-testid="cookie-manage">Manage</button>
      </div>
    </div>
  );
}

export default function NovaLayout() {
  return (
    <div className="theme-c min-h-screen flex flex-col">
      <NovaFonts />
      <StatusBar />
      <Header />
      <main className="flex-1" data-testid="main-content">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="bottom-right" />
    </div>
  );
}
